import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import type { ApplicationTabStatus } from '../../lib/applicationDetailTabStatus';

type Props = {
  status: ApplicationTabStatus;
  selected: boolean;
  pulse: boolean;
  color: string;
};

/** Status bead — same footprint as pass/review dots; fail gets a subtle 3D highlight. */
export function TabStatusIndicator({ status, selected, pulse, color }: Props) {
  const theme = useTheme();
  const isFail = status === 'fail';

  return (
    <Box
      component={motion.span}
      aria-hidden
      animate={
        pulse
          ? { scale: [1, 1.25, 1], opacity: [0.7, 1, 0.7] }
          : isFail && !selected
            ? { boxShadow: [`0 0 0 0 ${alpha(color, 0.35)}`, `0 0 0 4px ${alpha(color, 0)}`] }
            : { scale: 1, opacity: 1 }
      }
      transition={
        pulse
          ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' }
          : isFail && !selected
            ? { duration: 2.2, repeat: Infinity, ease: 'easeOut' }
            : { duration: 0.15 }
      }
      sx={{
        width: isFail ? 8 : 7,
        height: isFail ? 8 : 7,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: color,
        ...(isFail
          ? {
              background: `radial-gradient(circle at 32% 28%, ${alpha(theme.palette.common.white, 0.72)} 0%, ${color} 42%, ${theme.palette.error.dark} 100%)`,
              boxShadow: selected
                ? `0 0 10px ${alpha(color, 0.55)}, inset 0 -1px 1px ${alpha(theme.palette.common.black, 0.2)}`
                : `0 1px 2px ${alpha(color, 0.45)}, inset 0 -1px 1px ${alpha(theme.palette.common.black, 0.12)}`,
            }
          : {
              boxShadow: selected ? `0 0 8px ${alpha(color, 0.65)}` : 'none',
            }),
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    />
  );
}
